import {
    manageRouteCheckboxAssignments
} from '@containers/activity-router/routes/helpers/assignment/assignment-selected/route-assignments-selected';
import {
    CheckAssignmentEligibility
} from '@containers/activity-router/routes/helpers/assignment/assignment-selected/route-assignments-selected.types';
import {
    updateGridCellCounters
} from '@containers/activity-router/routes/helpers/grid/counters/grid-cell-counters/grid-cell-counters';
import {
    isInstructionHubBlockedAssignment
} from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub-helpers';
import {
    InstructionHubBlockedKeys
} from '@containers/activity-router/routes/helpers/instruction-hub/instruction-hub.types';
import { getStudentKey } from '@containers/activity-router/routes/helpers/keys/key-generator';
import { toggleGridCells } from '@containers/activity-router/routes/helpers/slice/grid/slice-grid-helpers';
import {
    ActivityId,
    IsGridCellVisible,
    StudentId,
    TopicId,
    ToggleRouteActivityGridCheckboxParams,
    ToggleRouteStudentGridCheckboxParams,
    ToggleRouteTopicGridCheckboxParams
} from '@containers/activity-router/routes/helpers/slice/slice.types';
import {
    GridCells,
    GridCounters
} from '@containers/activity-router/routes/helpers/slice/topic/topic-slice.types';

type ToggleEligibleGridCellsParams = {
    gridCells: GridCells
    gridCounters: GridCounters
    isChecked: boolean
    checkAssignmentEligibility: CheckAssignmentEligibility
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
};

function toggleEligibleGridCells ({
    gridCells,
    gridCounters,
    isChecked,
    checkAssignmentEligibility,
    instructionHubBlockedKeys
}: ToggleEligibleGridCellsParams) {
    Object.keys(gridCells).forEach((studentKey: StudentId) => {
        Object.keys(gridCells[studentKey]).forEach((topicKey: TopicId) => {
            Object.keys(gridCells[studentKey][topicKey]).forEach((activityKey: ActivityId) => {
                const canToggleCell = checkAssignmentEligibility({
                    studentKey,
                    topicKey,
                    activityKey
                }) && !isInstructionHubBlockedAssignment({
                    instructionHubBlockedKeys,
                    studentKey,
                    activityKey
                });

                if (!canToggleCell || gridCells[studentKey][topicKey][activityKey].isSelected === isChecked) return;

                gridCells[studentKey][topicKey][activityKey].isSelected = isChecked;
                gridCounters = updateGridCellCounters({
                    counters: gridCounters,
                    isSelected: isChecked,
                    studentId: studentKey,
                    topicId: topicKey,
                    activityId: activityKey
                });
            });
        });
    });

    return {
        gridCells,
        gridCounters
    };
}

type ToggleRouteVisibleGridCellsParams = Pick<ToggleRouteStudentGridCheckboxParams, 'state' | 'manageSelectedAssignments'> & {
    isCellIncluded: CheckAssignmentEligibility
    instructionHubBlockedKeys?: InstructionHubBlockedKeys
};

// A filter can hide some of a row or topic's cells, so the checkbox only changes the visible ones and
// decides from them: hidden selections are neither added nor removed.
const toggleRouteVisibleGridCells = ({
    state,
    manageSelectedAssignments,
    isCellIncluded,
    instructionHubBlockedKeys
}: ToggleRouteVisibleGridCellsParams) => {
    const isCellToggleable: CheckAssignmentEligibility = (cell) => isCellIncluded(cell) && !isInstructionHubBlockedAssignment({
        instructionHubBlockedKeys,
        studentKey: cell.studentKey,
        activityKey: cell.activityKey
    });
    const includedCells = Object.keys(state.gridCells).flatMap((studentKey) =>
        Object.keys(state.gridCells[studentKey]).flatMap((topicKey) =>
            Object.keys(state.gridCells[studentKey][topicKey])
                .map((activityKey) => ({
                    studentKey,
                    topicKey,
                    activityKey
                }))
                .filter(isCellToggleable)));
    // Matches the grid checkboxes, which show partial as checked: any visible selection means clear.
    const isChecked = !includedCells.some(({ studentKey, topicKey, activityKey }) =>
        state.gridCells[studentKey][topicKey][activityKey].isSelected);

    let {
        gridCells,
        gridCounters
    } = state;

    includedCells.forEach(({ studentKey, topicKey, activityKey }) => {
        if (gridCells[studentKey][topicKey][activityKey].isSelected !== isChecked) {
            ({
                gridCells,
                gridCounters
            } = toggleGridCells({
                gridCells,
                gridCounters,
                studentId: studentKey,
                topicId: topicKey,
                activityId: activityKey,
                isSelected: isChecked
            }));
        }
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells,
        isChecked,
        checkAssignmentEligibility: isCellToggleable,
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};

export const getIsCellInTopics = (topicKeys?: TopicId[]): IsGridCellVisible | undefined =>
    topicKeys && (({ topicKey }) => topicKeys.includes(topicKey));

export const toggleRouteStudentGridCheckbox = ({
    payload,
    state,
    manageSelectedAssignments,
    isCellVisible
}: ToggleRouteStudentGridCheckboxParams) => {
    const {
        student,
        isChecked,
        instructionHubBlockedKeys
    } = payload;
    const checkAssignmentEligibility: CheckAssignmentEligibility = ({ studentKey }) =>
        studentKey === getStudentKey({ id: student.userProfileId.toString() });

    if (isCellVisible) {
        return toggleRouteVisibleGridCells({
            state,
            manageSelectedAssignments,
            isCellIncluded: (cell) => checkAssignmentEligibility(cell) && isCellVisible(cell),
            instructionHubBlockedKeys
        });
    }

    const {
        gridCells,
        gridCounters
    } = toggleEligibleGridCells({
        gridCells: state.gridCells,
        gridCounters: state.gridCounters,
        isChecked,
        checkAssignmentEligibility,
        instructionHubBlockedKeys
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells: state.gridCells,
        isChecked,
        checkAssignmentEligibility: (params) =>
            checkAssignmentEligibility(params) && !isInstructionHubBlockedAssignment({
                instructionHubBlockedKeys,
                studentKey: params.studentKey,
                activityKey: params.activityKey
            }),
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};

export const toggleRouteTopicGridCheckbox = ({
    payload,
    state,
    manageSelectedAssignments,
    isCellVisible
}: ToggleRouteTopicGridCheckboxParams) => {
    const {
        topicId,
        isChecked,
        instructionHubBlockedKeys
    } = payload;
    const checkAssignmentEligibility: CheckAssignmentEligibility = ({ topicKey }) => topicKey === topicId;

    if (isCellVisible) {
        return toggleRouteVisibleGridCells({
            state,
            manageSelectedAssignments,
            isCellIncluded: (cell) => checkAssignmentEligibility(cell) && isCellVisible(cell),
            instructionHubBlockedKeys
        });
    }

    const {
        gridCells,
        gridCounters
    } = toggleEligibleGridCells({
        gridCells: state.gridCells,
        gridCounters: state.gridCounters,
        isChecked,
        checkAssignmentEligibility,
        instructionHubBlockedKeys
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells: state.gridCells,
        isChecked,
        checkAssignmentEligibility: (params) =>
            checkAssignmentEligibility(params) && !isInstructionHubBlockedAssignment({
                instructionHubBlockedKeys,
                studentKey: params.studentKey,
                activityKey: params.activityKey
            }),
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};

export const toggleRouteActivityGridCheckbox = ({
    payload,
    state,
    manageSelectedAssignments
}: ToggleRouteActivityGridCheckboxParams) => {
    const {
        activityId,
        isChecked,
        instructionHubBlockedKeys
    } = payload;
    const checkAssignmentEligibility: CheckAssignmentEligibility = ({ activityKey }) => activityKey === activityId;
    const {
        gridCells,
        gridCounters
    } = toggleEligibleGridCells({
        gridCells: state.gridCells,
        gridCounters: state.gridCounters,
        isChecked,
        checkAssignmentEligibility,
        instructionHubBlockedKeys
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells: state.gridCells,
        isChecked,
        checkAssignmentEligibility: (params) =>
            checkAssignmentEligibility(params) && !isInstructionHubBlockedAssignment({
                instructionHubBlockedKeys,
                studentKey: params.studentKey,
                activityKey: params.activityKey
            }),
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};
